'use client';
import {
  proformApiMutation,
  proformApiQuery,
  type ProformApiCommand,
  type ProformApiCommandQuerySettings,
} from '@eliadmin/core/src/api/proform/proform-api';
import type {
  ProductConf,
  ProductConfCols,
  ProductConfId,
  ProductDetail,
  ProductFolieSpec,
} from '@eliadmin/core/src/api/proform/proform-types';
import { createContextStore } from '@eliadmin/core/src/stores/context-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import type { StringOrNumber } from '@eliadmin/core/src/types/misc-types';
import {
  GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS,
  getAttributesFromConf,
  parseProformNumber,
} from '@eliadmin/core/src/utils/proform-utils';
import {
  useMutation,
  useMutationState,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  getConfiguratorMutationScope,
  synchronizeConfiguratorMutation,
} from './configurator-mutation-sync';

export type FieldDataItem = {
  kod: string;
  kod_grp?: string;
  popis: string;
  pic?: string;
  html?: string;
  smer_let?: string;
  folie_spec?: string;
  zada_spec?: string;
  sklo_spec?: string;
  pant_spec?: string;
  kod_pantu?: string;
  poradi_zobrazeni?: string;
  prov_sklo?: string;
} & (
  | {
      pic: string;
    }
  | {
      html: string;
    }
);

export type EnhancedProductConf = ProductConf & {
  root: ProductConf['root'] & {
    smako_conf_folie_spec: ProductFolieSpec['root']['smako_conf_folie_spec'];
    smako_conf_zada_spec: {
      zada_spec: ProductFolieSpec['root']['smako_conf_folie_spec']['folie_spec'];
    };
  };
};

export type ProductConfIdColumn = keyof ProductConfId['root']['smako_conf']['column'];

export type ConfiguratorState = {
  cislo_nomenklatury: string;
  id_shop: number;
  user_konf_id: StringOrNumber;
  cart_hash?: string | null;
  returnPath: string;
  productConf: EnhancedProductConf;
  productDetail: ProductDetail;
  productCols: ProductConfCols;
  errorFields: Record<string, string | boolean>;
  // selectedFieldData: {
  //   [K in ProductConfIdColumn]?: FieldDataItem | null;
  // };
};

export type ConfiguratorActions = {
  getMutationContext: () => Pick<
    ConfiguratorState,
    'cislo_nomenklatury' | 'id_shop' | 'user_konf_id'
  >;
  setCisloNomenklatury: (cislo_nomenklatury: string) => void;
  setUserKonfId: (user_konf_id: StringOrNumber) => void;
  // setSelectFieldData: (key: ProductConfIdColumn, value: FieldDataItem | null) => void;
  setErrorField: (key: string, value: string | boolean | null) => void;
  clearErrorFields: () => void;
};

const [ConfiguratorProvider, useConfiguratorStore] = createContextStore<
  ConfiguratorState,
  ConfiguratorActions
>(
  {
    cislo_nomenklatury: '',
    id_shop: 3,
    user_konf_id: '1',
    cart_hash: null,
    returnPath: '/',
    productConf: {} as EnhancedProductConf,
    productDetail: {} as ProductDetail,
    productCols: {} as ProductConfCols,
    errorFields: {},
    // selectedFieldData: {},
  },
  (set, get) => ({
    getMutationContext: () => {
      const { cislo_nomenklatury, id_shop, user_konf_id } = get();
      return { cislo_nomenklatury, id_shop, user_konf_id };
    },
    setCisloNomenklatury: (cislo_nomenklatury: string) => set({ cislo_nomenklatury }),
    setUserKonfId: (user_konf_id: StringOrNumber) => set({ user_konf_id }),
    // setSelectFieldData: (key: ProductConfIdColumn, value: FieldDataItem | null) => {
    //   set({
    //     selectedFieldData: {
    //       ...get().selectedFieldData,
    //       [key]: value,
    //     },
    //   });
    // },
    setErrorField: (key: string, value: string | boolean | null) => {
      const errorFields = get().errorFields;

      if (value === null) {
        delete errorFields[key];
        set({ errorFields });
        return;
      }

      set({
        errorFields: {
          ...errorFields,
          [key]: value,
        },
      });
    },

    clearErrorFields: () => {
      set({ errorFields: {} });
    },
  }),
);

export function useErrSync(column: string, err: boolean | string) {
  const error = useConfiguratorStore(s => s.errorFields[column]);
  const setErrors = useConfiguratorStore(s => s.setErrorField);

  useEffect(() => {
    if (!error && err) {
      setErrors(column, true);
    } else if (error && !err) {
      setErrors(column, null);
    }
  }, [error, err, setErrors, column]);
}

function useProformQueryOptions<TCommand extends ProformApiCommand>(
  command: TCommand,
  query: Omit<
    ProformApiCommandQuerySettings<TCommand>['request'],
    'session_e_mail' | 'id_shop' | 'cislo_nomenklatury'
  >,
) {
  const me = useCustomerStore(s => s.me?.customer);
  if (!me) {
    throw new Error('[useProformQueryOptions] customer is undefined, please login');
  }
  const cislo_nomenklatury = useConfiguratorStore(s => s.cislo_nomenklatury);
  const id_shop = useConfiguratorStore(s => s.id_shop);

  const fullQuery = {
    ...query,
    cislo_nomenklatury,
    session_e_mail: me.email,
    id_shop,
  } as any;

  return {
    queryKey: ['configurator', command, me.email, fullQuery],
    fullQuery,
  };
}

function useProformQuery<TCommand extends ProformApiCommand>(
  command: TCommand,
  query: Omit<
    ProformApiCommandQuerySettings<TCommand>['request'],
    'session_e_mail' | 'id_shop' | 'cislo_nomenklatury'
  >,
) {
  const { queryKey, fullQuery } = useProformQueryOptions(command, query);

  return useSuspenseQuery({
    queryKey,
    queryFn: () => proformApiQuery(command, fullQuery),
  });
}

export function useSetValueMutationPending() {
  return useMutationState({
    filters: { mutationKey: ['SET_PRODUCT_CONF'] },
    select: s => s.state.status === 'pending',
  }).some(Boolean);
}

export function useSetValueMutation() {
  const me = useCustomerStore(s => s.me?.customer);
  if (!me) {
    throw new Error('[useSetValueMutation] customer is undefined, please login');
  }
  const queryClient = useQueryClient();
  const id_shop = useConfiguratorStore(s => s.id_shop);
  const cislo_nomenklatury = useConfiguratorStore(s => s.cislo_nomenklatury);
  const getMutationContext = useConfiguratorStore(s => s.getMutationContext);
  const setUserKonfId = useConfiguratorStore(s => s.setUserKonfId);

  return useMutation({
    mutationKey: ['SET_PRODUCT_CONF'],
    scope: {
      id: getConfiguratorMutationScope({
        cislo_nomenklatury,
        id_shop,
        e_mail: me.email,
        user_konf_id: '',
      }),
    },
    mutationFn: (opts: { value: string | 'reset'; sloupec: ProductConfIdColumn }) => {
      const context = { ...getMutationContext(), e_mail: me.email };
      return synchronizeConfiguratorMutation({
        queryClient,
        context,
        fillingSelection:
          opts.sloupec === 'user_grp_skla' || opts.sloupec === 'user_kod_skla'
            ? { column: opts.sloupec, value: opts.value }
            : undefined,
        mutate: () =>
          proformApiMutation('SET_PRODUCT_CONF', {
            ...context,
            hodnota: opts.value,
            sloupec: opts.sloupec,
          }),
        fetchConfiguration: ({ e_mail, ...saved }) =>
          proformApiQuery('GET_PRODUCT_CONF_ID', { ...saved, session_e_mail: e_mail }),
        setConfigurationId: setUserKonfId,
      });
    },
  });
}

/**
 * Rotates the configurator id, while keeping the same product configuration
 */
export function useConfiguratorIdRotate(props: { onSuccess?: (newConfId: string) => void } = {}) {
  const user_konf_id = useConfiguratorStore(s => s.user_konf_id);
  // const setUserConfId = useConfiguratorStore(s => s.setUserKonfId);
  const productCols = useConfiguratorStore(s => s.productCols);
  const setUserKonfId = useConfiguratorStore(s => s.setUserKonfId);
  const cislo_nomenklatury = useConfiguratorStore(s => s.cislo_nomenklatury);

  const { data: productConfId } = useProformQuery('GET_PRODUCT_CONF_ID', {
    user_konf_id,
  });

  const me = useCustomerStore(s => s.me?.customer);

  const mutation = useMutation({
    onSuccess(data) {
      if (data) {
        setUserKonfId(data);
        props.onSuccess?.(data);
      }
    },
    mutationFn: async () => {
      if (!me) return;
      const attributes = getAttributesFromConf(
        productConfId,
        productCols,
        false,
        /**
         * We want to keep the quantity when rotating the configurator id
         */
        GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS.filter(v => v !== 'mnozstvi'),
      );

      const getOptions = (sloupec: string, value: any, user_konf_id: string) => {
        return {
          cislo_nomenklatury,
          e_mail: me!.email,
          hodnota: isNaN(parseProformNumber(value)) ? value : parseProformNumber(value),
          id_shop: 3,
          sloupec,
          user_konf_id,
        };
      };

      let user_konf_id = '1';

      for (const attribute of attributes) {
        const res = await proformApiMutation(
          'SET_PRODUCT_CONF',
          getOptions(attribute.attribute_name, attribute.name, user_konf_id),
        ).catch(() => null);

        if (
          user_konf_id === '1' &&
          res?.root.user_konf_id &&
          user_konf_id !== res.root.user_konf_id
        ) {
          user_konf_id = res.root.user_konf_id;
        }
      }

      if (user_konf_id === '1') {
        throw new Error('Failed to rotate configurator id');
      }

      return user_konf_id;
    },
  });

  return mutation;
}

function useConfiguratorValue(key: string) {
  const user_konf_id = useConfiguratorStore(s => s.user_konf_id);

  const productDetail = useConfiguratorStore(s => s.productDetail);
  const defaultValues = productDetail.root.smako_nom_default.default;

  const { data: productConfId } = useProformQuery('GET_PRODUCT_CONF_ID', {
    user_konf_id,
  });

  const mutation = useSetValueMutation();

  const value = getValueFromConfigId(productConfId, key, defaultValues);

  const onChange = (value: string) => {
    mutation.mutate({ value, sloupec: key as ProductConfIdColumn });
  };

  return {
    value,
    onChange,
    isMutating: mutation.isPending,
    mutationError: mutation.error,
    defaultValues,
    productConfId,
  };
}

function getValueFromConfigId(
  productConf: ProductConfId,
  key: string,
  defaultValues: ProductDetail['root']['smako_nom_default']['default'],
) {
  // @ts-expect-error TODO: type this
  const value = productConf?.root.smako_conf.column[key] ?? defaultValues[key] ?? '';
  return value as string;
}

function useCleanConfiguratorMutation() {
  const id_shop = useConfiguratorStore(s => s.id_shop);
  const cislo_nomenklatury = useConfiguratorStore(s => s.cislo_nomenklatury);
  const getMutationContext = useConfiguratorStore(s => s.getMutationContext);
  const setUserKonfId = useConfiguratorStore(s => s.setUserKonfId);
  const queryClient = useQueryClient();
  const me = useCustomerStore(s => s.me?.customer);
  if (!me) {
    throw new Error('[useCleanConfiguratorMutation] customer is undefined, please login');
  }
  return useMutation({
    mutationKey: ['SET_PRODUCT_CONF'],
    scope: {
      id: getConfiguratorMutationScope({
        cislo_nomenklatury,
        id_shop,
        e_mail: me.email,
        user_konf_id: '',
      }),
    },
    mutationFn: () => {
      const context = { ...getMutationContext(), e_mail: me.email };
      return synchronizeConfiguratorMutation({
        queryClient,
        context,
        mutate: () =>
          proformApiMutation('SET_PRODUCT_CONF', {
            ...context,
            hodnota: 'reset',
            sloupec: 'xcmd',
          }),
        fetchConfiguration: ({ e_mail, ...saved }) =>
          proformApiQuery('GET_PRODUCT_CONF_ID', { ...saved, session_e_mail: e_mail }),
        setConfigurationId: setUserKonfId,
      });
    },
  });
}

export {
  ConfiguratorProvider,
  getValueFromConfigId,
  useCleanConfiguratorMutation,
  useConfiguratorStore,
  useConfiguratorValue,
  useProformQuery,
};
